import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface AccentColor {
  id: string;
  name: string;
  accent: string;
  hover: string;
  light: string;
  rgb: string;
  darkHover: string;
  darkLight: string;
}

export const ACCENT_PALETTE: AccentColor[] = [
  {
    id: "pink",
    name: "Rosa Fucsia",
    accent: "#E91E63",
    hover: "#C2185B",
    light: "#FCE4EC",
    rgb: "233, 30, 99",
    darkHover: "#F06292",
    darkLight: "#4A0023",
  },
  {
    id: "violet",
    name: "Violeta Eléctrico",
    accent: "#8B5CF6",
    hover: "#7C3AED",
    light: "#EDE9FE",
    rgb: "139, 92, 246",
    darkHover: "#A78BFA",
    darkLight: "#2E1065",
  },
  {
    id: "cyan",
    name: "Cian Neón",
    accent: "#06B6D4",
    hover: "#0891B2",
    light: "#ECFEFF",
    rgb: "6, 182, 212",
    darkHover: "#22D3EE",
    darkLight: "#083344",
  },
  {
    id: "emerald",
    name: "Esmeralda Vibrante",
    accent: "#10B981",
    hover: "#059669",
    light: "#D1FAE5",
    rgb: "16, 185, 129",
    darkHover: "#34D399",
    darkLight: "#064E3B",
  },
  {
    id: "amber",
    name: "Ámbar Radiante",
    accent: "#F59E0B",
    hover: "#D97706",
    light: "#FEF3C7",
    rgb: "245, 158, 11",
    darkHover: "#FBBF24",
    darkLight: "#78350F",
  },
  {
    id: "sunset",
    name: "Naranja Atardecer",
    accent: "#FF5722",
    hover: "#E64A19",
    light: "#FBE9E7",
    rgb: "255, 87, 34",
    darkHover: "#FF8A65",
    darkLight: "#4E1A0B",
  },
  {
    id: "indigo",
    name: "Índigo Profundo",
    accent: "#3B82F6",
    hover: "#2563EB",
    light: "#DBEAFE",
    rgb: "59, 130, 246",
    darkHover: "#60A5FA",
    darkLight: "#1E3A8A",
  },
];

export function applyAccentToDom(accentId: string, isDark: boolean) {
  const selected = ACCENT_PALETTE.find((c) => c.id === accentId) || ACCENT_PALETTE[0];
  const root = document.documentElement;
  root.style.setProperty("--accent", selected.accent);
  root.style.setProperty("--color-accent", selected.accent);
  root.style.setProperty("--accent-rgb", selected.rgb);
  root.style.setProperty("--color-accent-rgb", selected.rgb);

  if (isDark) {
    root.style.setProperty("--accent-hover", selected.darkHover);
    root.style.setProperty("--color-accent-hover", selected.darkHover);
    root.style.setProperty("--accent-light", selected.darkLight);
  } else {
    root.style.setProperty("--accent-hover", selected.hover);
    root.style.setProperty("--color-accent-hover", selected.hover);
    root.style.setProperty("--accent-light", selected.light);
  }

  syncWindowTheme(selected, isDark);
}

// Sincroniza el color de la ventana del PWA (meta theme-color + manifest via SW)
function syncWindowTheme(selected: AccentColor, isDark: boolean) {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = isDark ? selected.darkLight : selected.accent;

  const theme = {
    themeColor: isDark ? selected.darkLight : selected.accent,
    backgroundColor: isDark ? "#121212" : "#FFFFFF",
  };
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready
      .then((reg) => reg.active?.postMessage({ type: "MAILFORGE_THEME", ...theme }))
      .catch(() => {});
  }
}

interface ThemeState {
  isDark: boolean;
  accentColor: string;
  toggleTheme: () => void;
  setTheme: (dark: boolean) => void;
  setAccentColor: (colorId: string) => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      isDark: false,
      accentColor: "pink",
      toggleTheme: () =>
        set((state) => {
          const next = !state.isDark;
          document.documentElement.classList.toggle("dark", next);
          applyAccentToDom(state.accentColor, next);
          return { isDark: next };
        }),
      setTheme: (dark: boolean) =>
        set((state) => {
          document.documentElement.classList.toggle("dark", dark);
          applyAccentToDom(state.accentColor, dark);
          return { isDark: dark };
        }),
      setAccentColor: (colorId: string) =>
        set((state) => {
          applyAccentToDom(colorId, state.isDark);
          return { accentColor: colorId };
        }),
    }),
    {
      name: "mailforge-theme",
      onRehydrateStorage: () => (state) => {
        if (state) {
          if (state.isDark) {
            document.documentElement.classList.add("dark");
          }
          applyAccentToDom(state.accentColor || "pink", Boolean(state.isDark));
        }
      },
    },
  ),
);
